import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Palette, Plus, Trash2, ArrowLeft, ExternalLink, Signature,
  Camera, Speaker, Sparkles, Monitor, Video,
} from "lucide-react";
import { toast } from "sonner";

const LUT_META = {
  camera_lut:  { label: "Camera LUT",  Icon: Camera,   color: "#6B4EFF" },
  show_lut:    { label: "Show LUT",    Icon: Palette,  color: "#F59E0B" },
  monitor_lut: { label: "Monitor LUT", Icon: Monitor,  color: "#0EA5E9" },
  sound_stem:  { label: "Sound Stem",  Icon: Speaker,  color: "#10B981" },
  vfx_plate:   { label: "VFX Plate",   Icon: Sparkles, color: "#EC4899" },
};

export default function ProjectLUTs() {
  const { id } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [rows, setRows]       = useState([]);
  const [signed, setSigned]   = useState(0);
  const [pending, setPending] = useState(0);
  const [meta, setMeta]       = useState({lut_types: []});
  const [openAdd, setOpenAdd] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [form, setForm]       = useState(blankForm());

  function blankForm() {
    return {
      name: "", lut_type: "show_lut",
      source_camera: "", color_space: "",
      file_url: "",
      dop_signoff: false, dop_name: "", signoff_date: "",
      notes: "",
    };
  }

  async function load() {
    try {
      const [{ data: p }, { data: r }, { data: m }] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/projects/${id}/luts`),
        api.get(`/projects/postprod/meta`),
      ]);
      setProject(p.project || p);
      setRows(r.luts || []);
      setSigned(r.signed_off || 0);
      setPending(r.pending || 0);
      setMeta(m);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load LUTs");
    }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [id]);

  async function add(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/projects/${id}/luts`, {
        name:          form.name,
        lut_type:      form.lut_type,
        source_camera: form.source_camera,
        color_space:   form.color_space,
        file_url:      form.file_url.trim(),
        dop_signoff:   form.dop_signoff,
        dop_name:      form.dop_name,
        signoff_date:  form.signoff_date,
        notes:         form.notes,
      });
      toast.success("LUT registered");
      setOpenAdd(false);
      setForm(blankForm());
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Add failed");
    } finally { setSaving(false); }
  }

  async function toggleSignoff(row) {
    try {
      await api.patch(`/projects/${id}/luts/${row.id}`, {
        dop_signoff: !row.dop_signoff,
        signoff_date: !row.dop_signoff ? new Date().toISOString().slice(0, 10) : "",
      });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    }
  }

  async function remove(rowId) {
    if (!window.confirm("Delete this LUT / post asset entry?")) return;
    try {
      await api.delete(`/projects/${id}/luts/${rowId}`);
      toast.success("Removed");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Remove failed");
    }
  }

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8" data-testid="project-luts-page">
      <button
        onClick={() => nav(`/p/${id}`)}
        className="text-[10px] tracking-[0.25em] uppercase text-white/55 hover:text-white inline-flex items-center gap-1.5"
        data-testid="back-to-project">
        <ArrowLeft size={12} /> Back to Project
      </button>

      <div className="mt-3 flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">Post-Production · LUTs &amp; Post Assets</div>
          <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tight" data-testid="luts-title">
            {project?.title || "LUT Tracker"}
          </h1>
          <div className="mt-2 text-sm text-white/55" data-testid="luts-summary">
            {rows.length} asset{rows.length===1?"":"s"} · {signed} DoP-signed · {pending} awaiting sign-off
          </div>
        </div>
        <Button onClick={() => setOpenAdd(true)}
          className="btn-themed rounded-full uppercase tracking-[0.22em] text-xs h-9 px-4"
          data-testid="luts-add-btn">
          <Plus size={14} className="mr-1.5" /> Add LUT / Asset
        </Button>
      </div>

      {rows.length === 0 && (
        <div className="mt-8 cs-card rounded-2xl p-10 text-center" data-testid="luts-empty">
          <Palette size={28} className="mx-auto text-white/40" />
          <div className="mt-3 text-sm text-white/60">No LUTs or post assets registered yet.</div>
          <div className="mt-1 text-xs text-white/45">
            Start with your Show LUT from the DoP, then add monitor and camera LUTs as they arrive.
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div className="mt-6 grid md:grid-cols-2 gap-4" data-testid="luts-grid">
          {rows.map(r => (
            <LUTCard key={r.id} row={r} onToggleSignoff={toggleSignoff} onRemove={remove} />
          ))}
        </div>
      )}

      <Dialog open={openAdd} onOpenChange={setOpenAdd}>
        <DialogContent className="bg-[#0c0c0e] border-white/10 sm:max-w-2xl" data-testid="luts-add-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight">Register LUT / post asset</DialogTitle>
            <DialogDescription className="text-white/55">Paste the file URL (Drive · Dropbox · S3) — DI can pull it from there.</DialogDescription>
          </DialogHeader>
          <form onSubmit={add} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Name *">
                <Input required value={form.name} data-testid="lut-name"
                  onChange={e => setForm({...form, name: e.target.value})}
                  placeholder="e.g. Show Look Warm 01"
                  className="bg-black/40 border-white/15" />
              </Field>
              <Field label="Type">
                <Select value={form.lut_type} onValueChange={(v)=>setForm({...form, lut_type: v})}>
                  <SelectTrigger className="bg-black/40 border-white/15" data-testid="lut-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {meta.lut_types.map(t => (
                      <SelectItem key={t} value={t}>{LUT_META[t]?.label || t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Source Camera">
                <Input value={form.source_camera} data-testid="lut-camera"
                  onChange={e => setForm({...form, source_camera: e.target.value})}
                  placeholder="Arri Alexa Mini LF · RED V-Raptor · Sony Venice"
                  className="bg-black/40 border-white/15" />
              </Field>
              <Field label="Colour Space">
                <Input value={form.color_space} data-testid="lut-cspace"
                  onChange={e => setForm({...form, color_space: e.target.value})}
                  placeholder="LogC4 · RWG/Log3G10 · S-Log3"
                  className="bg-black/40 border-white/15" />
              </Field>
              <div className="sm:col-span-2">
                <Field label="File URL">
                  <Input value={form.file_url} data-testid="lut-url"
                    onChange={e => setForm({...form, file_url: e.target.value})}
                    placeholder="https://drive.google.com/…"
                    className="bg-black/40 border-white/15" />
                </Field>
              </div>
              <Field label="DoP Name">
                <Input value={form.dop_name} data-testid="lut-dop"
                  onChange={e => setForm({...form, dop_name: e.target.value})}
                  className="bg-black/40 border-white/15" />
              </Field>
              <Field label="Sign-off Date">
                <Input type="date" value={form.signoff_date} data-testid="lut-signoff-date"
                  onChange={e => setForm({...form, signoff_date: e.target.value})}
                  className="bg-black/40 border-white/15" />
              </Field>
              <div className="sm:col-span-2">
                <label className="flex items-center gap-2 text-xs text-white/70" data-testid="lut-signoff-toggle">
                  <input type="checkbox" checked={form.dop_signoff}
                    onChange={e => setForm({...form, dop_signoff: e.target.checked})} />
                  <Signature size={12} /> DoP has signed off on this asset
                </label>
              </div>
              <div className="sm:col-span-2">
                <Field label="Notes">
                  <Textarea value={form.notes} data-testid="lut-notes"
                    onChange={e => setForm({...form, notes: e.target.value})}
                    placeholder="Warm skin tones · match tests done · use only for interiors"
                    className="bg-black/40 border-white/15 min-h-[70px]" />
                </Field>
              </div>
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="ghost" onClick={()=>setOpenAdd(false)} data-testid="lut-cancel">Cancel</Button>
              <Button type="submit" disabled={saving} className="btn-themed rounded-full uppercase tracking-[0.22em] text-xs px-5"
                data-testid="lut-submit">
                {saving ? "Saving…" : "Register asset"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LUTCard({ row, onToggleSignoff, onRemove }) {
  const meta = LUT_META[row.lut_type] || { label: row.lut_type, Icon: Palette, color: "#888" };
  const Icon = meta.Icon;
  return (
    <div className="cs-card rounded-2xl p-5" data-testid={`lut-card-${row.id}`}
      style={{ borderColor: `${meta.color}22` }}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: `${meta.color}22`, color: meta.color }}>
            <Icon size={16} />
          </div>
          <div className="min-w-0">
            <div className="font-semibold truncate" data-testid={`lut-name-${row.id}`}>{row.name}</div>
            <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: meta.color }}>{meta.label}</div>
          </div>
        </div>
        <button onClick={() => onRemove(row.id)} className="p-1.5 text-white/35 hover:text-red-400"
          data-testid={`lut-remove-${row.id}`}>
          <Trash2 size={13} />
        </button>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
        <div>
          <div className="text-[9px] uppercase tracking-[0.22em] text-white/45">Source</div>
          <div className="text-white/80 truncate">{row.source_camera || "—"}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-[0.22em] text-white/45">Colour Space</div>
          <div className="text-white/80 truncate">{row.color_space || "—"}</div>
        </div>
      </div>
      {row.notes && (
        <div className="mt-3 text-[11px] text-white/60 line-clamp-2">{row.notes}</div>
      )}
      <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
        <button onClick={() => onToggleSignoff(row)}
          className={`text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full border ${
            row.dop_signoff ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" : "border-white/15 text-white/60 hover:border-white/40"
          }`}
          data-testid={`lut-signoff-${row.id}`}>
          <Signature size={11} className="inline mr-1" />
          {row.dop_signoff
            ? `DoP signed${row.dop_name ? ` · ${row.dop_name}` : ""}${row.signoff_date ? ` · ${row.signoff_date}` : ""}`
            : "Mark DoP signed"}
        </button>
        {row.file_url && (
          <a href={row.file_url} target="_blank" rel="noreferrer"
            className="text-[10px] uppercase tracking-[0.2em] text-white/70 hover:text-white inline-flex items-center gap-1"
            data-testid={`lut-download-${row.id}`}>
            Download <ExternalLink size={10} />
          </a>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
